/**
 * Phase 9.4 — 👥 Following Feed
 * -----------------------------
 * Renders the current user's followed-artist track stream from
 * /api/discover/following. Anon users get a friendly login CTA;
 * authed-but-no-follows users get an "explore artists" CTA.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { getCoverUrl, getProfilePhotoUrl } from "@/lib/mediaUrls";
import { getDisplayPlays, formatDisplayPlays } from "@/lib/promoPlays";
import { PlayButton } from "@/components/player/PlayButton";
import PresenceDot from "@/components/PresenceDot";

const FollowingFeed = () => {
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/discover/following?limit=20");
        if (!cancelled) setState({ loading: false, data: r.data });
      } catch (_e) {
        if (!cancelled) setState({ loading: false, data: null });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (state.loading) {
    return (
      <div className="min-h-[200px] flex items-center justify-center">
        <p className="text-pink-200/50">Loading your feed…</p>
      </div>
    );
  }

  const data = state.data || {};
  if (data.requires_auth) {
    return (
      <Card className="p-6 text-center bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/30" data-testid="following-feed-anon">
        <div className="text-5xl mb-2">👥</div>
        <h3 className="text-xl font-bold text-white mb-1">Follow your favorite artists</h3>
        <p className="text-pink-200/70 text-sm mb-4">
          Sign in to see new drops, beam announcements, and updates from artists you follow.
        </p>
        <Link to="/auth"><Button className="bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold">Sign in</Button></Link>
      </Card>
    );
  }
  if (data.empty_reason === "no_follows" || (data.items || []).length === 0) {
    return (
      <Card className="p-6 text-center bg-white/5 border-white/10" data-testid="following-feed-empty">
        <div className="text-5xl mb-2">🌱</div>
        <h3 className="text-xl font-bold text-white mb-1">Your feed is empty</h3>
        <p className="text-pink-200/70 text-sm mb-4">
          Follow some artists to see their new drops, lives, and collabs here.
        </p>
        <Link to="/community"><Button variant="outline" className="text-pink-200 border-pink-500/40">Explore artists</Button></Link>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="following-feed-grid">
      {data.items.map((t) => {
        const trackPayload = {
          submission_id: t.submission_id,
          track_title: t.title,
          artist_name: t.artist?.artist_name,
          user_id: t.artist?.user_id,
          cover_url: getCoverUrl(t),
          display_plays: t.display_plays,
          music_url: t.music_url,
        };
        return (
        <Link
          key={t.submission_id}
          to={`/track/${t.submission_id}`}
          className="block group"
          data-testid={`following-card-${t.submission_id}`}
        >
          <Card className="p-3 bg-white/5 border-white/10 hover:bg-white/10 hover:border-pink-500/30 transition-all">
            <div className="relative">
              <img
                src={getCoverUrl(t)}
                alt={t.title}
                className="w-full aspect-square rounded-lg object-cover mb-3"
                onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=400&h=400&fit=crop"; }}
              />
              <PlayButton
                track={trackPayload}
                queue={data.items.map((x) => ({
                  submission_id: x.submission_id,
                  track_title: x.title,
                  artist_name: x.artist?.artist_name,
                  user_id: x.artist?.user_id,
                  cover_url: getCoverUrl(x),
                  display_plays: x.display_plays,
                  music_url: x.music_url,
                }))}
                sourceLabel="Following"
                className="absolute bottom-5 right-2 opacity-90 shadow-xl"
                size={38}
                testIdSuffix="following"
              />
            </div>
            <h3 className="text-white font-bold truncate">{t.title}</h3>
            <div className="flex items-center gap-2 mt-1">
              {t.artist?.profile_photo && (
                <img src={getProfilePhotoUrl(t.artist)} alt="" className="w-5 h-5 rounded-full object-cover" />
              )}
              <p className="text-pink-200/70 text-xs truncate flex-1 min-w-0">{t.artist?.artist_name || "Artist"}</p>
              {t.artist?.user_id && (
                <PresenceDot userId={t.artist.user_id} size="sm" />
              )}
            </div>
            <div className="flex gap-3 text-[10px] text-pink-200/50 mt-2">
              <span>▶ {formatDisplayPlays(getDisplayPlays(t))}</span>
              {t.gift_total > 0 && <span>🎁 {t.gift_total.toLocaleString()}</span>}
            </div>
          </Card>
        </Link>
      );})}
    </div>
  );
};

export default FollowingFeed;
